import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from 'react'
import SearchService from './SearchService'
import {
  IHandleQuickSearchResult,
  ISearchProviderConfig,
  IQuickSearchResultTypo,
  ISearchAndFilterProduct,
  IQuickSearchResultCollection,
  IQuickSearchResultSuggestion,
  IQuickSearchResultContent,
  ISearchFilter,
  ISearchFilterOption,
  IQuickSearchResultPage,
  ISearchFilterRangeOption,
  IActiveCollection,
  IGetProductsResult,
  IGetCmsContentResult,
} from './types'
import { useDebouncedCallback } from 'use-debounce'
import updateLocationQuery from './helpers/updateLocationQuery'
import getCurrentSelectedFilters from './helpers/getCurrentSelectedFilters'

import FILTER_SEARCH from './consts'
import extenders from './extenders'
export interface IFFetchContext {
  children: any
  config: ISearchProviderConfig
  activeCollection: IActiveCollection
}

interface ContextValue {
  activeCollection: IActiveCollection
  searchTerm: string
  quickSearchTerm: string
  setQuickSearchTerm: Dispatch<SetStateAction<string>>
  sortKey: string
  setSortKey: Dispatch<SetStateAction<string>>
  pageIndex: number
  isSearching: boolean
  quickSearchResult: IHandleQuickSearchResult
  quickSearchResultsTypos: IQuickSearchResultTypo[]
  quickSearchResultsProducts: ISearchAndFilterProduct[]
  quickSearchResultsCollections: IQuickSearchResultCollection[]
  quickSearchResultsPages: IQuickSearchResultPage[]
  quickSearchResultsSuggestions: IQuickSearchResultSuggestion[]
  quickSearchResultsContent: IQuickSearchResultContent[]
  searchFilters: ISearchFilter[]
  filtersLoading: boolean
  productSearchResult: IGetProductsResult
  productSearchResultsState: IGetProductsResult[]
  cmsSearchResults: IGetCmsContentResult
  pendingProductCount: number
  inputEventHandlers: {
    handleSearchInputChange: any
    handleSearchInputKeyPress: any
    handleSearchSuggestionSelected: any
    handleSearchFocus: any
    handleUpdateRangeOption: (
      filterIndex: number,
      optionIndex: number,
      selectedMinimum: number,
      selectedMaximum: number
    ) => void
    handleToggleMultiOption: (filterIndex: number, optionIndex: number) => void
    handleToggleSingleOption: (
      filterIndex: number,
      optionIndex: number,
      value: boolean
    ) => void
    handleUpdatePageIndex: (
      pageIndex: number,
      triggerFiltering: boolean,
      silent: boolean
    ) => void
    handleSortByChange: any
    handleClearFilters: () => void
    handleClearSpecificFilter: (filterIndex: number) => void
    handleToggleFilterOpen: (filterIndex: number, value?: boolean) => void
    handleApply: () => void
  }
  config: ISearchProviderConfig
}

export const FILTER_QUERY_PARAM_PREFIX = 'filter_'
export const FILTER_QUERY_PARAM_SEPARATOR = ','

export const SORT_TYPE_VALS = Object.values(FILTER_SEARCH.SORT_TYPES)

export const SearchContext: React.Context<ContextValue> = createContext(null)

export const SearchProvider: React.FC<IFFetchContext> = ({
  children,
  config,
  activeCollection
}) => {
  // control
  const [isSearching, setIsSearching] = useState<boolean>(false)
  // input
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [quickSearchTerm, setQuickSearchTerm] = useState<string>('')
  const [pageIndex, setPageIndex] = useState<number>(null)

  // data
  const [quickSearchResult, setQuickSearchResult] =
    useState<IHandleQuickSearchResult>(null)
  const [quickSearchResultsTypos, setQuickSearchResultsTypos] =
    useState<IQuickSearchResultTypo[]>(null)
  const [quickSearchResultsProducts, setQuickSearchResultsProducts] =
    useState<ISearchAndFilterProduct[]>(null)
  const [quickSearchResultsCollections, setQuickSearchResultsCollections] =
    useState<IQuickSearchResultCollection[]>(null)
  const [quickSearchResultsSuggestions, setQuickSearchResultsSuggestions] =
    useState<IQuickSearchResultSuggestion[]>(null)
  const [quickSearchResultsContent, setQuickSearchResultsContent] =
    useState<IQuickSearchResultContent[]>(null)
  const [quickSearchResultsPages, setQuickSearchResultsPages] =
    useState<IQuickSearchResultPage[]>(null)

  const [filtersToUpdate, setFiltersToUpdate] = useState<
    {
      filterIndex: number
      optionIndex: number
    }[]
  >([])

  const [searchFilters, setSearchFilters] = useState<ISearchFilter[]>(null)

  const [sortKey, setSortKey] = useState<string>(null)

  const [productSearchResult, setProductSearchResult] =
    useState<IGetProductsResult>(null)
  const [productSearchResultsState, setProductResultsState] = useState<
    IGetProductsResult[]
  >([])

  const [cmsSearchResults, setCmsSearchResults] =
    useState<IGetCmsContentResult>(null)

  const [filtersLoading, setFiltersLoading] = useState<boolean>(false)

  const [pendingProductCount, setPendingProductCount] = useState(0)

  const updateFilters = (newFilters: ISearchFilter[]): void => {
    const updatedFilters = searchFilters.map((filter) => {
      const newFilter = newFilters.find(
        ({ identifier }) => identifier === filter.identifier
      )

      if (filter.type === FILTER_SEARCH.FILTER_TYPES.RANGE) {
        // keep range the same
        // @TODO, we could update the mins and maxes here?
        return filter
      }

      const oldOptions = filter.options as ISearchFilterOption[] // as not range type
      const newOptions = newFilter
        ? (newFilter.options as ISearchFilterOption[])
        : []

      return {
        ...filter,
        options: oldOptions.map((option) => {
          const newOption = newOptions.find(
            ({ identifier }) => identifier && identifier === option.identifier
          )
          return {
            ...option,
            records: newOption ? newOption.records : 0
          }
        })
      }
    })

    setSearchFilters([...updatedFilters])
  }

  const wipeLiquidProducts = (): void => {
    const liquidProducts = document.querySelector('[data-liquid-products]')
    if (liquidProducts) {
      liquidProducts.innerHTML = ''
    }
  }

  const refreshFiltersOnly = () => {
    SearchService.methods
      .getProductResults(config, searchFilters, activeCollection, {
        term: searchTerm,
        pageIndex: pageIndex,
        sortKey: sortKey
      })
      .then((response) => {
        if (response.filters) {
          updateFilters(response.filters)
        }
        setPendingProductCount(response.total)
        setProductSearchResult(response)
      })
  }

  const executeSearch = useDebouncedCallback((term) => {
    SearchService.methods.handleSearch(term, config).then((response) => {
      setIsSearching(false)
      setQuickSearchResult(response)
      setQuickSearchResultsTypos(response.typos)
      setQuickSearchResultsProducts(response.products)
      setQuickSearchResultsCollections(response.collections)
      setQuickSearchResultsSuggestions(response.suggestions)
      setQuickSearchResultsPages(response.pages)
      setQuickSearchResultsContent(null) // @todo
    })
  }, 1000)

  const outputProductData = (productData, checkPageIndex) => {
    const emptyResponse: IGetProductsResult = {
      records: 0,
      total: 0,
      raw: null,
      products: [],
      filters: []
    }
    const state = productSearchResultsState
    if (checkPageIndex) {
      state[checkPageIndex] = productData
    }
    // console.log(state)
    if (config.rLoadMoreMode === 'loadMore' && checkPageIndex) {
      // infinite scroll set all productResult data (state[0] is undefined, hence .filter)
      const allData = state
        .filter((def) => def)
        .reduce((all, thisData) => {
          return {
            raw: [...(all.raw ?? []), ...(thisData.raw ?? [])],
            records: all.records + thisData.records,
            total: thisData.total,
            products: [...all.products, ...thisData.products],
            filters: thisData.filters
          }
        }, emptyResponse)
      setProductSearchResult(allData)
    } else {
      // pagination, only want specific page of products
      setProductSearchResult(productData)
    }
    setPendingProductCount(productData.total)
  }

  const executeFilteredSearch = useDebouncedCallback((forceIndex = null) => {
    const checkPageIndex = forceIndex ?? pageIndex
    if (checkPageIndex && productSearchResultsState[checkPageIndex]) {
      wipeLiquidProducts()
      outputProductData(
        productSearchResultsState[checkPageIndex],
        checkPageIndex
      )
      setFiltersLoading(false)
    } else {
      SearchService.methods
        .getProductResults(config, searchFilters, activeCollection, {
          term: searchTerm,
          pageIndex: checkPageIndex,
          sortKey: sortKey
        })
        .then((response) => {
          wipeLiquidProducts()
          outputProductData(response, checkPageIndex)
          if (response.filters) {
            updateFilters(response.filters)
            setFiltersLoading(false)
          }
          if (checkPageIndex) {
            const state = productSearchResultsState
            setProductResultsState([...state])
            setFiltersLoading(false)
          }
        })
    }
  }, 1000)

  const setLoadingAndExecuteFilteredSearch = () => {
    const liquidProducts = document.querySelector('[data-liquid-products]')

    if (liquidProducts) {
      liquidProducts.classList.add('is-loading')
    }

    setFiltersLoading(true)

    executeFilteredSearch.callback()
  }

  const updatePageIndex = (
    pageIndex: number,
    triggerFiltering: boolean,
    silent: boolean,
    historyMethod: 'pushState' | 'replaceState' = 'pushState'
  ) => {
    let forceSilentIndex = null
    if (!silent) {
      // update state
      setPageIndex(pageIndex)

      // set url param
      updateLocationQuery({
        key: 'page',
        historyMethod,
        value: pageIndex.toString(),
        action: 'set'
      })
    } else {
      // silent update
      forceSilentIndex = pageIndex
    }

    // re-filter
    if (triggerFiltering) {
      executeFilteredSearch.callback(forceSilentIndex)
    }
  }

  const eventHandlers = {
    filterUpdated: (params: { filterIndex: number; optionIndex: number }) => {
      // get the filter
      const filter = searchFilters[params.filterIndex]
      if (['SINGLE_OPTION', 'MULTI_OPTION'].includes(filter.type)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const options: ISearchFilterOption[] = (
          filter.options as ISearchFilterOption[]
        )
          .map((option: ISearchFilterOption) => {
            return option.selected ? option.identifier : false
          })
          .filter((option) => option)

        if (options.length) {
          updateLocationQuery({
            key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
            value: options.join(FILTER_QUERY_PARAM_SEPARATOR),
            action: 'set'
          })
        } else {
          if (options.length > 0) {
            updateLocationQuery({
              key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
              value: options.join(FILTER_QUERY_PARAM_SEPARATOR),
              action: 'set'
            })
          } else {
            updateLocationQuery({
              key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
              action: 'delete'
            })
          }
        }
      } else if (['RANGE'].includes(filter.type)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const option: ISearchFilterRangeOption =
          filter.options[params.optionIndex]

        updateLocationQuery({
          key: `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}`,
          value: `${option.selectedMinimum}:${option.selectedMaximum}`,
          action:
            option.selectedMinimum === option.minimum &&
            option.selectedMaximum === option.maximum
              ? 'delete'
              : 'set'
        })
      } else {
        throw 'Unhandled filter type'
      }

      updatePageIndex(1, false, false, 'replaceState')
      // invalidate paginated state
      setProductResultsState([])
    },

    sortByUpdated: (params: { sortKey: string }) => {
      if (!SORT_TYPE_VALS.includes(params.sortKey)) {
        console.info('Incorrect sort type')
      }

      updateLocationQuery({
        key: 'sort',
        value: params.sortKey,
        action: 'set'
      })
      setSortKey(params.sortKey)
      updatePageIndex(1, false, false, 'replaceState')
      // invalidate paginated state
      setProductResultsState([])
    }
  }

  /**
   * Input event handlers
   */
  const inputEventHandlers = {
    handleSearchInputChange: (e) => {
      const query = e.target.value

      setQuickSearchTerm(query)

      // result all search results if the search field has been emptied
      if (query.length === 0) {
        setQuickSearchResult(null)
        setQuickSearchResultsTypos(null)
        setQuickSearchResultsProducts(null)
        setQuickSearchResultsCollections(null)
        setQuickSearchResultsSuggestions(null)
        setQuickSearchResultsContent(null)
        return
      }

      setIsSearching(true)

      executeSearch.callback(query)
    },

    handleSearchInputKeyPress: (e) => {
      if (e.keyCode === 13) {
        window.location.href = `/search?q=${quickSearchTerm}`
      }
    },

    handleSearchFocus: (searchTerm) => {
      if(searchTerm && searchTerm.length) {
        setQuickSearchTerm(searchTerm)
        setIsSearching(true)
        executeSearch.callback(searchTerm)
      }
    },

    handleSearchSuggestionSelected: (index) => {
      // remove any HTML which may have been added to the suggestion result
      const suggestion = quickSearchResultsSuggestions[index].value.replace(
        /(<([^>]+)>)/gi,
        ''
      )
      setIsSearching(true)
      setSearchTerm(suggestion)
      executeSearch.callback(suggestion)
    },

    handleUpdateRangeOption: (
      filterIndex: number,
      optionIndex: number,
      selectedMinimum: number,
      selectedMaximum: number
    ) => {
      const updatedOption = [...searchFilters][filterIndex].options[
        optionIndex
      ] as ISearchFilterRangeOption
      updatedOption.selectedMinimum = selectedMinimum
      updatedOption.selectedMaximum = selectedMaximum
      updatedOption.selected = !(
        updatedOption.selectedMinimum === updatedOption.minimum &&
        updatedOption.selectedMaximum === updatedOption.maximum
      )

      const updatedSearchFilters = [...searchFilters]

      updatedSearchFilters[filterIndex].options[optionIndex] = updatedOption

      setSearchFilters([...updatedSearchFilters])

      setFiltersToUpdate([
        ...filtersToUpdate,
        {
          filterIndex: filterIndex,
          optionIndex: optionIndex
        }
      ])

      refreshFiltersOnly()
    },

    handleToggleMultiOption: (filterIndex: number, optionIndex: number) => {
      const updatedOption = [...searchFilters][filterIndex].options[
        optionIndex
      ] as ISearchFilterOption

      updatedOption.selected = !updatedOption.selected

      const updatedSearchFilters = [...searchFilters]

      updatedSearchFilters[filterIndex].options[optionIndex] = updatedOption

      setSearchFilters([...updatedSearchFilters])

      setFiltersToUpdate([
        ...filtersToUpdate,
        {
          filterIndex: filterIndex,
          optionIndex: optionIndex
        }
      ])

      refreshFiltersOnly()
    },

    handleToggleSingleOption: (
      filterIndex: number,
      optionIndex: number,
      value: boolean
    ) => {
      const updatedFilterOptions = [...searchFilters][filterIndex]
        .options as ISearchFilterOption[]

      for (let i = 0; i < updatedFilterOptions.length; i++) {
        updatedFilterOptions[i].selected = false
      }

      updatedFilterOptions[optionIndex].selected = value

      const updatedSearchFilters = [...searchFilters]
      updatedSearchFilters[filterIndex].options = updatedFilterOptions

      setSearchFilters([...updatedSearchFilters])

      setFiltersToUpdate([
        ...filtersToUpdate,
        {
          filterIndex: filterIndex,
          optionIndex: optionIndex
        }
      ])

      refreshFiltersOnly()
    },

    handleUpdatePageIndex: (
      pageIndex: number,
      triggerFiltering: boolean,
      silent: boolean
    ) => {
      updatePageIndex(pageIndex, triggerFiltering, silent, 'pushState')
    },

    handleSortByChange: (val: string) => {
      eventHandlers.sortByUpdated({
        sortKey: val
      })
      setLoadingAndExecuteFilteredSearch()
    },

    handleClearFilters: () => {
      const indices = []
      const updatedFilters = [...searchFilters]
      updatedFilters.forEach((filter, index: number) => {
        const ticked = (filter.options as ISearchFilterOption[]).map(
          ({ selected }, i) => ({ selected, i })
        )
        if (ticked.length) {
          for (const selected of ticked) {
            if (!selected.selected) continue
            indices.push({ index, int: selected.i })
          }
        }
      })
      if (!indices.length) {
        return null
      }
      indices.forEach(({ index, int }) =>
        inputEventHandlers.handleToggleMultiOption(index, int)
      )
      //Handle clearing Rangeslider
      const range = indices.find(
        ({ index }) => updatedFilters[index].type === 'RANGE'
      )
      if (range) {
        const rangeSlider = updatedFilters[range.index]
        const option = rangeSlider.options[
          range.int
        ] as ISearchFilterRangeOption
        option.selectedMaximum = option.maximum
        option.selectedMinimum = option.minimum
        //Update URL
        updateLocationQuery({
          key: `${FILTER_QUERY_PARAM_PREFIX}${rangeSlider.identifier}`,
          action: 'delete'
        })
      }
      setSearchFilters(updatedFilters)
    },
    handleClearSpecificFilter: (filterIndex: number) => {
      const updatedFilters = [...searchFilters]
      const filterToClear = updatedFilters[filterIndex]
      if (filterToClear.type === 'RANGE') {
        const option = filterToClear.options[0] as ISearchFilterRangeOption
        option.selectedMaximum = option.maximum
        option.selectedMinimum = option.minimum
      } else {
        const indices = []
        const ticked = (filterToClear.options as ISearchFilterOption[]).map(
          ({ selected }, i) => ({ selected, i })
        )
        if (ticked.length) {
          for (const selected of ticked) {
            if (!selected.selected) continue
            indices.push({ int: selected.i })
          }
        }

        if (!indices.length) {
          return null
        }
        indices.forEach(({ int }) =>
          inputEventHandlers.handleToggleMultiOption(filterIndex, int)
        )
      }

      setSearchFilters(updatedFilters)
      refreshFiltersOnly()
    },

    handleToggleFilterOpen: (filterIndex: number, value?: boolean) => {
      const updatedFilters = [...searchFilters].map((filter, index) => {
        if (index !== filterIndex) {
          //close others
          return {
            ...filter,
            open: false
          }
        } else {
          return {
            ...filter,
            open: typeof value !== 'undefined' ? value : !filter.open
          }
        }
      })
      setSearchFilters(updatedFilters)
    },
    handleApply: () => {
      // execute filter updated event

      filtersToUpdate.map(({ filterIndex, optionIndex }) => {
        eventHandlers.filterUpdated({
          filterIndex: filterIndex,
          optionIndex: optionIndex
        })
      })
      setLoadingAndExecuteFilteredSearch()
    }
  }

  const getCurrentUrlWithTagSet = (tag: string, val: boolean) => {
    const path = window.location.pathname.split('/').filter((part) => part)
    let newPath = `/${path.join('/')}`
    if (val && path[path.length - 1] !== tag) {
      // needs to add
      newPath = `/${path.join('/')}/${tag}`
    } else if (!val && path[path.length - 1] === tag) {
      // needs to remove
      newPath = `/${path.slice(0, -1).join('/')}`
    }
    return `${window.location.origin}${newPath}${window.location.search}${window.location.hash}`
  }

  const handlePopState = () => {
    const params = new URLSearchParams(window.location.search)
    const pageParam = params.get('page')
    const page =
      pageParam && !isNaN(parseInt(pageParam)) ? parseInt(pageParam) : 1
    if (page) {
      updatePageIndex(page, true, false, 'replaceState')
    }
  }

  //on render
  useEffect(() => {
    //check if url includes filtered as getting weird safari bug, SearchFilteredProducts component was kicking in on PLP when back clicked
    if (window.location.pathname.includes('filtered')) {
      window.addEventListener('popstate', () => handlePopState())
    }
  }, [])

  /**
   * Initialise Search Filters
   */
  useEffect(() => {
    // parse query params for search context
    const params = new URLSearchParams(window.location.search)

    let executeSearch = false
    let localSearchTerm = null
    if (!searchTerm) {
      if (params.get('q')) {
        localSearchTerm = params.get('q')
        setSearchTerm(localSearchTerm)
        executeSearch = true
      }
    }

    if (!pageIndex) {
      if (params.get('page')) setPageIndex(parseInt(params.get('page')))
      else setPageIndex(1)
    }

    if (!sortKey) {
      if (params.get('sort')) {
        setSortKey(params.get('sort'))
        executeSearch = true
      }
    }

    if (!cmsSearchResults && params.get('q')) {
      SearchService.methods
        .getCmsContent(params.get('q'), config)
        .then((response) => {
          setCmsSearchResults(response)
        })
        .catch((error) => {
          console.error(error)
          // @todo handle error for getting cms content
        })
    }

    // @todo we should only really be calling this if we're on a search rules / collection page
    if (!searchFilters) {
      SearchService.methods
        .getFilters(config, activeCollection, localSearchTerm)
        .then((response) => {
          // parse relevant filters from URL
          const selectedFilters = []
          params.forEach((values, key) => {
            if (key.startsWith(FILTER_QUERY_PARAM_PREFIX)) {
              values.split(',').forEach((value) => {
                selectedFilters.push({
                  filterKey: key,
                  optionKey: value
                })
              })
            }
          })

          // Initialise filter states
          if (selectedFilters.length > 0) {
            selectedFilters.forEach(
              (selectedFilter: { filterKey: string; optionKey: string }) => {
                // find the filter index
                const filterIndex = response.filters.findIndex((filter) => {
                  return (
                    `${FILTER_QUERY_PARAM_PREFIX}${filter.identifier}` ===
                    selectedFilter.filterKey
                  )
                })

                if (filterIndex === -1) return

                // set initially opened state of the filter (for FE display)
                response.filters[filterIndex].initiallyOpen = true

                // toggle the option
                if (
                  ['SINGLE_OPTION', 'MULTI_OPTION'].includes(
                    response.filters[filterIndex].type
                  )
                ) {
                  // find the option index
                  const optionIndex = response.filters[
                    filterIndex
                  ].options.findIndex((option) => {
                    return option.identifier === selectedFilter.optionKey
                  })

                  if (optionIndex >= 0) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    response.filters[filterIndex].options[
                      optionIndex
                    ].selected = true
                    executeSearch = true
                  }
                } else if (
                  ['RANGE'].includes(response.filters[filterIndex].type)
                ) {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  response.filters[filterIndex].options[0].selectedMinimum =
                    selectedFilter.optionKey.split(':')[0]

                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  response.filters[filterIndex].options[0].selectedMaximum =
                    selectedFilter.optionKey.split(':')[1]

                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  response.filters[filterIndex].options[0].selected = true

                  executeSearch = true
                }
              }
            )
          }
          setSearchFilters(response.filters)

          if (executeSearch) setLoadingAndExecuteFilteredSearch()
        })
    }

    const selectedFilters = searchFilters
      ? getCurrentSelectedFilters(searchFilters)
      : []
    const updateUrlWithTag = Boolean(
      productSearchResult &&
        activeCollection.handle &&
        (selectedFilters.length > 0 || sortKey)
    )

    const url = getCurrentUrlWithTagSet(
      config.searchFilterDummyTag,
      updateUrlWithTag
    )
    if (window.history) {
      window.history.replaceState({ ...window.history.state }, '', url)
    }
  }, [productSearchResult, searchTerm, sortKey])

  useEffect(() => {
    if (productSearchResult) {
      //extend the data if we need to
      let result = { ...productSearchResult }
      for (const extender of extenders) {
        if (typeof extender.init !== 'undefined') {
          extender.init()
        }
        if (typeof extender.extendGetProductsResult !== 'undefined') {
          result = extender.extendGetProductsResult(result)
        }
      }
      // search page - update the page title and collection title
      if (
        document.querySelector('.template-search') &&
        productSearchResult?.total
      ) {
        const resultsTotalStr = productSearchResult.total.toString()
        document.title = document.title.replace(
          /\d+/,
          resultsTotalStr
        )
      }
        
      const counterElems = document.querySelectorAll('[data-search-results-counter]')

      if(!counterElems || !counterElems.length) return
      counterElems.forEach((elem: HTMLElement) => {
        elem.innerText = productSearchResult.total.toString()
      })

      setProductSearchResult(result)
    }
  }, [productSearchResultsState])

  return (
    <SearchContext.Provider
      value={{
        activeCollection,
        searchTerm,
        quickSearchTerm,
        setQuickSearchTerm,
        pageIndex,
        quickSearchResult,
        quickSearchResultsTypos,
        quickSearchResultsSuggestions,
        quickSearchResultsProducts,
        quickSearchResultsCollections,
        quickSearchResultsContent,
        quickSearchResultsPages,
        searchFilters,
        filtersLoading,
        sortKey,
        setSortKey,
        productSearchResult,
        productSearchResultsState,
        cmsSearchResults,
        isSearching,
        inputEventHandlers,
        config,
        pendingProductCount
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

const useSearch = (): ContextValue => useContext(SearchContext)

export default useSearch
